import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Table,
  Tag,
  Space,
  Select,
  Button,
  Typography,
  Spin,
  Empty,
  Tooltip,
  Timeline,
} from 'antd';
import {
  HistoryOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchProductPriceHistoryRequest } from '../../redux/actions/priceHistoryActions';

const { Text } = Typography;
const { Option } = Select;

const fmtMoney = (val) =>
  val != null ? `₹ ${Number(val).toFixed(2)}` : '—';

const fmtDate = (val) =>
  val ? dayjs(val).format('DD MMM YYYY') : '—';

const fmtDateTime = (val) =>
  val ? dayjs(val).format('DD MMM YYYY, hh:mm A') : '—';

// ── Change indicator ──────────────────────────────────────────────────────────

const ChangeIndicator = ({ entry }) => {
  const isCreation = entry.old_rate === 0;
  if (isCreation) {
    return (
      <Tag color="blue" style={{ fontWeight: 500 }}>
        Initial Price
      </Tag>
    );
  }
  const pct = Number(entry.change_percent);
  if (pct > 0) {
    return (
      <Tag color="error" icon={<ArrowUpOutlined />} style={{ fontWeight: 600 }}>
        +{pct.toFixed(2)}%
      </Tag>
    );
  }
  if (pct < 0) {
    return (
      <Tag color="success" icon={<ArrowDownOutlined />} style={{ fontWeight: 600 }}>
        {pct.toFixed(2)}%
      </Tag>
    );
  }
  return (
    <Tag icon={<MinusOutlined />} color="default">
      No change
    </Tag>
  );
};

// ── Trend badge exported for ProductList use ──────────────────────────────────

export const PriceTrendBadge = ({ history }) => {
  if (!history || history.length < 2) return null;
  const latest = history[0];
  const prev   = history[1];
  if (latest.old_rate === 0) return null;

  const pct = Number(latest.change_percent);
  if (pct > 0) {
    return (
      <Tooltip title={`Last change: +${pct.toFixed(1)}% on ${fmtDate(latest.created_at)}`}>
        <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 4, fontWeight: 600 }}>
          ↑{pct.toFixed(1)}%
        </span>
      </Tooltip>
    );
  }
  if (pct < 0) {
    return (
      <Tooltip title={`Last change: ${pct.toFixed(1)}% on ${fmtDate(latest.created_at)}`}>
        <span style={{ color: '#52c41a', fontSize: 11, marginLeft: 4, fontWeight: 600 }}>
          ↓{Math.abs(pct).toFixed(1)}%
        </span>
      </Tooltip>
    );
  }
  return null;
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * ProductPriceHistory
 *
 * Props:
 *  open        — boolean
 *  onClose     — callback
 *  product     — { id, product_name, unit, vendors: [] }
 */
const ProductPriceHistory = ({ open, onClose, product }) => {
  const dispatch = useDispatch();
  const { productHistory, loading } = useSelector((s) => s.priceHistory);
  const [vendorFilter, setVendorFilter] = useState(undefined);

  const productId = product?.id;
  const history   = productHistory[productId] || [];
  const vendors   = product?.vendors || [];

  const load = (vid) => {
    if (!productId) return;
    const params = {};
    if (vid) params.vendor_id = vid;
    dispatch(fetchProductPriceHistoryRequest(productId, params));
  };

  useEffect(() => {
    if (open && productId) load(vendorFilter);
  }, [open, productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVendorChange = (val) => {
    setVendorFilter(val);
    load(val);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'date',
      width: 130,
      render: (v) => (
        <Tooltip title={fmtDateTime(v)}>
          <Text style={{ fontSize: 12 }}>{fmtDate(v)}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Old Rate',
      dataIndex: 'old_rate',
      key: 'old_rate',
      width: 110,
      align: 'right',
      render: (v, record) =>
        record.old_rate === 0 ? (
          <Text type="secondary" style={{ fontSize: 11 }}>New Product</Text>
        ) : (
          <Text style={{ color: '#8c8c8c', textDecoration: 'line-through', fontSize: 12 }}>
            {fmtMoney(v)}
          </Text>
        ),
    },
    {
      title: 'New Rate',
      dataIndex: 'new_rate',
      key: 'new_rate',
      width: 110,
      align: 'right',
      render: (v) => (
        <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{fmtMoney(v)}</Text>
      ),
    },
    {
      title: 'Change',
      key: 'change',
      width: 110,
      align: 'center',
      render: (_, record) => <ChangeIndicator entry={record} />,
    },
    {
      title: 'Changed By',
      dataIndex: 'changed_by_name',
      key: 'changed_by_name',
      width: 120,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Reason',
      dataIndex: 'change_reason',
      key: 'change_reason',
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>
          </Tooltip>
        ) : null,
    },
  ];

  const currentRate = history.length > 0 ? history[0].new_rate : product?.purchase_rate;

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined style={{ color: '#1890ff' }} />
          Price History — {product?.product_name}
          {product?.unit && (
            <Tag color="geekblue" style={{ fontSize: 11 }}>{product.unit}</Tag>
          )}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      width={760}
      destroyOnClose
    >
      {/* Current rate summary */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 14px',
          background: '#e6f7ff',
          borderRadius: 6,
          marginBottom: 16,
          border: '1px solid #91d5ff',
        }}
      >
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Current Purchase Rate</Text>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#0050b3' }}>
            {fmtMoney(currentRate)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Total Price Changes</Text>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1890ff' }}>
            {history.filter((h) => h.old_rate !== 0).length}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        {vendors.length > 0 && (
          <Select
            placeholder="Filter by vendor"
            allowClear
            style={{ width: 220 }}
            value={vendorFilter}
            onChange={handleVendorChange}
          >
            {vendors.map((v) => (
              <Option key={v.id} value={v.id}>{v.firm_name}</Option>
            ))}
          </Select>
        )}
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => load(vendorFilter)}
          loading={loading}
          style={{ marginLeft: 'auto' }}
        >
          Refresh
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : history.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No price history recorded yet for this product."
        />
      ) : (
        <Table
          columns={columns}
          dataSource={history}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 600 }}
          rowClassName={(record) => {
            if (record.old_rate === 0) return '';
            return Number(record.change_percent) > 0
              ? 'price-history-row-up'
              : Number(record.change_percent) < 0
              ? 'price-history-row-down'
              : '';
          }}
        />
      )}
    </Modal>
  );
};

export default ProductPriceHistory;
